// Offscreen canvas for measuring rendered text width without touching the DOM (used to fit the
// multi-select trigger summary to the available width, like the legacy chip-overflow logic did).
const measureCtx = typeof document !== "undefined" ? document.createElement("canvas").getContext("2d") : null;

export const measureTextWidth = (text: string, font: string): number => {
	if (!measureCtx) return text.length * 7; // deterministic fallback (SSR / no canvas)
	measureCtx.font = font;
	return measureCtx.measureText(text).width;
};

/**
 * Greedily fits as many names as will render within `avail` px (in `font`), reserving room for the
 * "+N more" suffix, and never showing more than `cap` names. Always shows at least one (it ellipsizes
 * via CSS if even one overflows). `avail <= 0` (not yet measured) shows all up to `cap`.
 */
export const fitNames = (names: string[], avail: number, font: string, cap?: number): { visible: string[]; hidden: number } => {
	if (names.length === 0) return { visible: [], hidden: 0 };
	const maxCount = cap && cap > 0 ? Math.min(cap, names.length) : names.length;
	const visible: string[] = [];
	for (let i = 0; i < maxCount; i += 1) {
		const candidate = [...visible, names[i]].join(", ");
		const remainderAfter = names.length - (i + 1);
		const suffix = remainderAfter > 0 ? ` +${remainderAfter} more` : "";
		if (avail > 0 && visible.length >= 1 && measureTextWidth(candidate + suffix, font) > avail) break;
		visible.push(names[i]);
	}
	return { visible, hidden: names.length - visible.length };
};

/**
 * Greedily fits chip PILLS within `avail` px (in `font`), reserving room for a trailing "+N" chip and
 * never exceeding `cap`. Returns how many chips to render. Always shows at least one (it clips via CSS if
 * even one overflows). `avail <= 0` (not yet measured) shows all up to `cap`. Chrome accounts for the pill
 * padding, the remove-icon, and the inter-chip gap so chips never paint under the trigger's caret.
 */
const CHIP_CHROME = 34;
const CHIP_GAP = 6;
export const fitChips = (labels: string[], avail: number, font: string, cap?: number): { visible: number; hidden: number } => {
	if (labels.length === 0) return { visible: 0, hidden: 0 };
	const maxCount = cap && cap > 0 ? Math.min(cap, labels.length) : labels.length;
	let used = 0;
	let visible = 0;
	for (let i = 0; i < maxCount; i += 1) {
		const chipW = measureTextWidth(labels[i], font) + CHIP_CHROME + CHIP_GAP;
		const remainderAfter = labels.length - (i + 1);
		const suffixW = remainderAfter > 0 ? measureTextWidth(`+${remainderAfter}`, font) + 12 : 0;
		if (avail > 0 && visible >= 1 && used + chipW + suffixW > avail) break;
		used += chipW;
		visible += 1;
	}
	return { visible, hidden: labels.length - visible };
};
